import React from 'react';
import { useTranslation } from 'react-i18next';
import { getBusinessUrl, getHelpUrl, isMainDomain, isSameOrigin } from '../../utils/domainUtils';

const GradientDivider = () => (
  <div className="lp-max-w-7xl lp-mx-auto lp-px-4 sm:lp-px-6 lg:lp-px-8">
    <div className="lp-h-px lp-w-full lp-bg-gradient-to-r lp-from-transparent lp-via-[#DDB889]/50 lp-to-transparent" />
  </div>
);

interface FooterProps {
  includeTop?: boolean;
}

const FooterBottom = () => {
  const { t } = useTranslation();

  const legalLinks = [
    { name: t('footer.links.privacyPolicy'), href: getBusinessUrl('/privacy') },
    { name: t('footer.links.termsOfUse'), href: getBusinessUrl('/terms') },
    { name: t('footer.links.help'), href: getHelpUrl() }
  ];

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (isMainDomain() && isSameOrigin(href)) {
      window.location.href = href;
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="lp-relative lp-bg-gradient-to-b lp-from-black lp-to-black/95">
      <GradientDivider />
      <div className="lp-max-w-7xl lp-mx-auto lp-px-4 sm:lp-px-6 lg:lp-px-8 lp-py-4 sm:lp-py-6">
        <div className="lp-flex lp-flex-col lp-space-y-3 sm:lp-space-y-0 sm:lp-flex-row sm:lp-justify-between sm:lp-items-center lp-text-sm lp-text-gray-400">
          {/* Copyright section - compact on mobile */}
          <div className="lp-text-center sm:lp-text-left lp-text-sm">
            <div className="lp-inline-flex lp-flex-wrap lp-justify-center sm:lp-justify-start lp-items-center lp-gap-1">
              <span>{t('footer.copyright')}</span>
                <a
                href={getBusinessUrl()}
                onClick={(e) => handleLinkClick(e, getBusinessUrl())}
                className="lp-text-[#DDB889] hover:lp-text-[#DDB889]/80 lp-transition-colors"
              >
                {t('footer.company')}
              </a>
              <span>. {t('footer.allRightsReserved')}</span>
            </div>
          </div>

          {/* Legal links - scrollable row on mobile */}
          <div className="lp-overflow-x-auto -lp-mx-4 lp-px-4 sm:lp-mx-0 sm:lp-px-0">
            <div className="lp-flex lp-items-center lp-justify-center sm:lp-justify-end lp-min-w-max">
              {legalLinks.map((link, index) => (
                <React.Fragment key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => handleLinkClick(e, link.href)}
                    className="lp-text-sm hover:lp-text-[#DDB889] lp-transition-colors lp-whitespace-nowrap lp-px-2 lp-py-1"
                  >
                    {link.name}
                  </a>
                  {index < legalLinks.length - 1 && (
                    <span className="lp-text-gray-600">|</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Location - moved to bottom on mobile */}
          <div className="lp-text-center lp-text-xs sm:lp-text-sm sm:lp-text-right">
            {t('footer.location')}
          </div>
        </div>
      </div>
    </div>
  );
};

const FooterTop = () => {
  const { t } = useTranslation();

  const footerSections = [
    {
      title: t('footer.sections.shopAndLearn'),
      links: ['Store', 'Mac', 'iPad', 'iPhone', 'Watch', 'Vision', 'AirPods']
    },
    {
      title: t('footer.sections.services'),
      links: ['Apple Music', 'Apple TV+', 'Apple Fitness+', 'Apple News+', 'Apple Arcade']
    },
    {
      title: t('footer.sections.store'),
      links: ['Find a Store', 'Genius Bar', 'Today at Apple', 'Apple Camp', 'Apple Store App']
    },
    {
      title: t('footer.sections.forBusiness'),
      links: ['Apple and Business', 'Shop for Business', 'Business Solutions', 'Partner Program']
    }
  ];

  return (
    <>
      <GradientDivider />
      <div className="lp-max-w-7xl lp-mx-auto lp-px-4 sm:lp-px-6 lg:lp-px-8 lp-py-6 sm:lp-py-12">
        <div className="lp-grid lp-grid-cols-2 sm:lp-grid-cols-2 lg:lp-grid-cols-4 lp-gap-6 lg:lp-gap-12">
          {footerSections.map((section) => (
            <div key={section.title} className="lp-space-y-3">
              <h3 className="lp-font-boska lp-text-base sm:lp-text-lg lp-text-[#DDB889]">
                {section.title}
              </h3>
              <ul className="lp-space-y-2">
                {section.links.map((link) => (
                  <li key={link}>
                    <a
                      href="#"
                      className="lp-text-xs sm:lp-text-sm lp-text-gray-400 hover:lp-text-[#DDB889] lp-transition-colors lp-duration-300"
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const Footer: React.FC<FooterProps> = ({ includeTop = true }) => {
  return (
    <footer className="lp-relative lp-bg-gradient-to-b lp-from-black lp-to-black/95">
      {includeTop && <FooterTop />}
      <FooterBottom />
    </footer>
  );
};

export default Footer;
